import type { ReactElement } from 'react';
import type { MomentumJourneyStage } from '../../lib/plan';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { EditableText } from '../../components/Editable';
import { artProductId, deliveryKind, momentumJourneyStage, pricedLine, productLabel } from '../../lib/plan';
import { useContentLibrary } from '../../state/ContentLibraryContext';
import { productDeviceAsset } from './assets';
import { MOMENTUM_STAGES } from './Motifs';
import { Frame, FunnelStrip, StageBar, SubCard } from './primitives';

const STAGE_LABEL: Record<MomentumJourneyStage, string> = Object.fromEntries(
  MOMENTUM_STAGES.map((stage) => [stage.key, stage.label]),
) as Record<MomentumJourneyStage, string>;

/**
 * product_education (brief §4 `11 channelEducation`): CONTENT+PANEL light,
 * ×N, optional per `document.options.showEducation`. Library content, not
 * per-client (brief: "Not editable per deck without forking the library
 * entry") — `productEducation[productId]` is read live from the content
 * library context on every render, never snapshotted onto the section, the
 * same rule momentum_v1's `ProductEducationPage` follows. `content.body`
 * still wins over the library description when a rep has written their own,
 * matching every other library-backed field in this app.
 */
export function ProductEducationPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const productId = section.productId;
  const { productName, education } = useContentLibrary();
  const edu = productId ? education(productId) : null;
  const label = productId ? (productName(productId) ?? productLabel(proposal, productId)) : section.title;
  const body = section.content.body ?? edu?.description;
  const priced = productId ? pricedLine(proposal, productId) : null;
  const kind = deliveryKind(priced);
  const stage = productId ? momentumJourneyStage(productId, kind) : null;

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-edu-root"
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-edu-body">
          <div className="mv3-eyebrow">{label} / Education</div>
          <div className="mv3-edu-headline">
            <EditableText
              value={section.content.headline || edu?.title || 'What this channel is built to do.'}
              field="headline"
              placeholder="What this channel is built to do."
              readOnly={readOnly}
              variant="headline"
              clampLines={2}
              onSave={(headline) => actions.saveContent(section, { headline })}
            />
          </div>
          {stage ? <StageBar surface="light">{STAGE_LABEL[stage]}</StageBar> : null}
          <div className="mv3-edu-layout">
            <div className="mv3-edu-role">
              <span className="mv3-eyebrow">The role in the mix</span>
              <div className="mv3-edu-role-body">
                <EditableText
                  value={body}
                  field="body"
                  placeholder="This product has no library description yet."
                  readOnly={readOnly}
                  clampLines={3}
                  onSave={(next) => actions.saveContent(section, { body: next })}
                />
              </div>
            </div>
            <SubCard tone="dark" className="mv3-edu-proof">
              <img
                src={productDeviceAsset(productId ? artProductId(proposal, productId) : productId)}
                alt=""
                aria-hidden="true"
                className="mv3-edu-proof-art"
              />
              {edu?.statistic ? (
                <div className="mv3-edu-proof-content">
                  <span className="mv3-edu-proof-value">{edu.statistic.value}</span>
                  <span className="mv3-edu-proof-label">{edu.statistic.label}</span>
                  {edu.statistic.source ? (
                    <span className="mv3-edu-proof-source">Source: {edu.statistic.source}</span>
                  ) : null}
                </div>
              ) : null}
            </SubCard>
          </div>
          <FunnelStrip activeStage={stage} className="mv3-edu-funnel" />
        </div>
      </Frame>
    </PageShell>
  );
}
